import React from "react";
import { Box, Typography, Chip, Divider, CircularProgress } from "@mui/material";
import { useQuery } from "@apollo/client/react";
import { STUDENT_AUDIT_HISTORY } from "../graphql/query";

// Story 2.1 (web): read-only audit / status history shown in the student detail
// (view mode). Admin / super-admin only — the resolver enforces this.
interface AuditEntry {
  id: string;
  action: string;
  oldStatus?: string | null;
  newStatus?: string | null;
  justification?: string | null;
  actorRole?: string | null;
  source: string;
  created_at: string;
}

interface AuditQueryResult {
  studentAuditHistory: {
    success: boolean;
    message?: string;
    data: AuditEntry[];
  };
}

const ACTION_LABEL: Record<string, string> = {
  STATUS_CHANGE: "Status change",
  SOFT_DELETE: "Deleted",
  SELF_DELETE: "Self-deleted",
  DELETE_SCHEDULED: "Deletion scheduled",
  ARCHIVE: "Archived",
  RESTORE: "Restored",
  PURGE: "Data purged",
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const StudentAuditHistory: React.FC<{ studentId: string }> = ({ studentId }) => {
  const { data, loading, error } = useQuery<AuditQueryResult>(STUDENT_AUDIT_HISTORY, {
    variables: { studentId },
    skip: !studentId,
    fetchPolicy: "network-only",
  });

  const entries = data?.studentAuditHistory?.data ?? [];

  return (
    <Box
      sx={{
        mt: 3,
        p: { xs: 2, sm: 2.5 },
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Typography sx={{ fontWeight: 800, color: "#1F3864", mb: 1.5 }}>
        Account History
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#667085" }}>
          <CircularProgress size={16} /> <span>Loading history…</span>
        </Box>
      )}

      {!loading && error && (
        <Typography sx={{ fontSize: "0.85rem", color: "#98A2B3" }}>
          History is unavailable.
        </Typography>
      )}

      {!loading && !error && entries.length === 0 && (
        <Typography sx={{ fontSize: "0.85rem", color: "#98A2B3" }}>
          No account history yet.
        </Typography>
      )}

      {!loading &&
        entries.map((e, idx) => (
          <Box key={e.id}>
            {idx > 0 && <Divider sx={{ my: 1.25 }} />}
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
              <Chip
                label={ACTION_LABEL[e.action] || e.action}
                size="small"
                sx={{ bgcolor: "#EEF2F8", color: "#1F3864", fontWeight: 600 }}
              />
              {(e.oldStatus || e.newStatus) && (
                <Typography sx={{ fontSize: "0.8rem", color: "#475467" }}>
                  {e.oldStatus || "—"} → <strong>{e.newStatus || "—"}</strong>
                </Typography>
              )}
              <Typography sx={{ fontSize: "0.75rem", color: "#98A2B3", ml: "auto" }}>
                {formatDate(e.created_at)} · {e.source}
                {e.actorRole ? ` (${e.actorRole})` : ""}
              </Typography>
            </Box>
            {e.justification && (
              <Typography sx={{ fontSize: "0.8rem", color: "#667085", mt: 0.5 }}>
                “{e.justification}”
              </Typography>
            )}
          </Box>
        ))}
    </Box>
  );
};

export default StudentAuditHistory;
